import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { isEqual, uniq } from "lodash";
import { Input, Checkbox, DatePicker } from "antd";
import { Search, ChevronUp, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { LEFT_FILETERS_SIZE } from "../config";

dayjs.extend(utc);
const { RangePicker } = DatePicker;
const CheckboxGroup = Checkbox.Group;

const CommonFilter = ({
  title,
  icon,
  dataList = [],
  onSearch,
  onDataChange,
  showNumberofRecords,
  showCheckAll,
  setOpenAdvancedModel,
  openVisibleFilter,
  isShow,
  isModal,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const existingParam = searchParams.get(title);
  const currentPage = searchParams.get("page");
  const [data, setData] = useState([]);
  const [viewMore, setViewMore] = useState(false);
  const minViewData = 36;
  const filteredQuery = useMemo(() => {
    const obj = {};
    for (const [key, value] of searchParams.entries()) {
      if (key !== "page" && key !== "size") {
        obj[key] = value;
      }
    }
    return obj;
  }, [searchParams]);

  const onDateRangeChange = (dates, dateStrings) => {
    if (dateStrings && dateStrings[0] !== "" && dateStrings[1] !== "") {
      const startDate = dayjs(dateStrings[0], "YYYY-MM-DD")
        .startOf("day")
        .utc()
        .format();
      const endDate = dayjs(dateStrings[1], "YYYY-MM-DD")
        .endOf("day")
        .utc()
        .format();

      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    } else {
      searchParams.delete("startDate");
      searchParams.delete("endDate");
    }

    searchParams.set("page", 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const dateValue = useMemo(() => {
    const sDate = searchParams.get("startDate");
    const eDate = searchParams.get("endDate");
    if (sDate && eDate) {
      return [dayjs(sDate), dayjs(eDate)];
    }
    return null;
  }, [searchParams]);

  const predefinedRanges = {
    Today: [dayjs(), dayjs()],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last 7 Days": [dayjs().subtract(6, "day"), dayjs()],
    "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
    "This Year": [dayjs().startOf("year"), dayjs().endOf("year")],
  };

  useEffect(() => {
    setData(dataList);
  }, [dataList]);

  // Get current values from query param
  let valuesArray = [];

  // Convert existingParam to array if not null
  if (existingParam) {
    valuesArray = Array.isArray(existingParam)
      ? existingParam
      : existingParam.split(",");
  }

  const idType = [
    "Industry",
    "Company Type",
    "Employee Count",
    "Revenue Range",
    "Executive Function",
    "Executive Level",
    "Lead Status",
    "Employee Size",
  ];

  const dataDumpType = ["Saved Search"];

  const onFilterChange = (val) => {
    const selectedValue = idType?.includes(title)
      ? val.target.value?.id?.toString()
      : dataDumpType?.includes(title)
        ? val.target.value?.dataDump
        : val.target.value?.name;
    const isChecked = val.target.checked;
    if (dataDumpType?.includes(title)) {
      if (isChecked) {
        const queryObject = JSON.parse(selectedValue);
        const queryString = new URLSearchParams(queryObject).toString();
        navigate(`${location.pathname}?${queryString}`, { replace: true });
      } else {
        navigate(location.pathname, { replace: true });
      }
    } else {
      if (isChecked) {
        if (val.target.value === "All") {
          const updatedValues = idType?.includes(title)
            ? dataList?.map((d) => d?.id?.toString())
            : dataList?.map((d) => d?.name?.toString());
          searchParams.set(title, updatedValues.join(","));
        } else {
          // Add new value uniquely
          const updatedValues = uniq([...valuesArray, selectedValue]);
          searchParams.set(title, updatedValues.join(","));
        }
      } else {
        // Remove unchecked value
        const filteredValues = valuesArray.filter((v) => v !== selectedValue);
        if (val.target.value === "All") {
          searchParams.delete(title);
        } else {
          if (filteredValues.length > 0) {
            searchParams.set(title, filteredValues.join(","));
          } else {
            searchParams.delete(title);
          }
        }
      }
      if (currentPage !== 1) {
        searchParams.set("page", 1);
      }
      // Update route with new query string
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
  };
  const showViewMore = data.length > LEFT_FILETERS_SIZE.length;

  return (
    <>
      <div
        className={`filterheader tw-text-text-primary la ${isShow && "show"}`}
        onClick={openVisibleFilter}
      >
        {!isModal && (
          <h2
            className={`side-filter ${!isModal && "tw-bg-[#fafafa]"} tw-flex tw-items-center tw-justify-between !tw-px-4`}
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              {icon}
              <span className="!tw-font-inter">{title}</span>
            </div>
            <div>
              {isShow ? (
                <ChevronUp size={16} className="tw-text-slate-500" />
              ) : (
                <ChevronDown size={16} className="tw-text-slate-500" />
              )}
            </div>
          </h2>
        )}
      </div>
      <div className="filteroptions">
        <div className={isModal ? "" : "searchbox"}>
          <Input
            placeholder="Search"
            className={
              isModal ? "tw-bg-secondary-color tw-border-secondary-color" : ""
            }
            prefix={
              <Search size={14} className="tw-text-slate-400" />
            }
            onChange={(event) => {
              const actualData = dataList;
              const filterdData = actualData?.filter((item) =>
                item?.name
                  .toLowerCase()
                  .includes(event.target.value.toLowerCase()),
              );
              setData(filterdData);
              onSearch && onSearch(event);
            }}
          />
        </div>
        {title === "Lead Time" && (
          <div className="tw-px-3 tw-pt-1.5 tw-pb-0">
            <div className="tw-text-[11px] tw-font-semibold tw-text-slate-400 tw-mb-1 tw-uppercase tw-tracking-wider">
              Custom Date Range
            </div>
            <RangePicker
              value={dateValue}
              ranges={predefinedRanges}
              placeholder={["Start date", "End date"]}
              needConfirm={true}
              onChange={onDateRangeChange}
              className="tw-w-full tw-rounded-lg tw-border-slate-200 tw-text-xs"
            />
          </div>
        )}
        {!!data.length && showCheckAll && (
          <div className="tw-flex tw-items-center tw-justify-end tw-my-1.5">
            <Checkbox
              value="All"
              className="tw-text-theme tw-font-semibold"
              checked={isEqual(
                dataList?.map((d) =>
                  idType?.includes(title) ? d?.id?.toString() : d?.name,
                ),
                valuesArray,
              )}
              onChange={onFilterChange}
            >
              Select all
            </Checkbox>
          </div>
        )}
        <CheckboxGroup
          onChange={(val) => onDataChange && onDataChange(val)}
          value={(dataList || [])?.filter((d) => {
            return dataDumpType?.includes(title)
              ? isEqual(JSON.parse(d?.dataDump), filteredQuery)
              : (valuesArray || []).includes(
                  idType?.includes(title) ? d?.id?.toString() : d?.name,
                );
          })}
        >
          <ul className="filter-options-vertical-list">
            {data?.map((item, index) => {
              if (showNumberofRecords && !viewMore) {
                return (
                  index < showNumberofRecords && (
                    <li key={`${item?.name}-${index}`}>
                      <Checkbox
                        value={item}
                        onChange={($event) => onFilterChange($event)}
                        className="tw-text-[#65686F] tw-text-xs"
                      >
                        {item.name}
                      </Checkbox>
                    </li>
                  )
                );
              } else {
                if (index + 1 > minViewData && !viewMore) {
                  return <></>;
                }
                return (
                  <li key={`${item?.name}-${index}`}>
                    <Checkbox
                      value={item}
                      onChange={($event) => onFilterChange($event)}
                      className="tw-text-[#65686F] tw-text-xs"
                    >
                      {item.name}
                    </Checkbox>
                  </li>
                );
              }
            })}
          </ul>
        </CheckboxGroup>
        {showViewMore && !isModal && (
          <div className="viewmore">
            <span
              onClick={() => setOpenAdvancedModel({ open: true, key: title })}
            >
              View More
            </span>
          </div>
        )}
        {data.length > minViewData && isModal && (
          <div className="advancemodel viewmore">
            <span
              onClick={() => {
                setViewMore(!viewMore);
              }}
            >
              {viewMore ? "Hide" : "View More"}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default CommonFilter;
